import {
  BodyCollection,
  Editor,
  FullscreenAbstractEditorHandler,
  FullscreenEditing,
  Plugin,
} from 'ckeditor5';

// this class is heavily based on the decoupled editor implementation in ckeditors source code
// deliberate calls out to private methods are done here to avoid cloning them into our repo

class MultirootFullscreenAdapter extends FullscreenAbstractEditorHandler {
  // It's not mandatory to override `#_editor` property, but that will help TypeScript to properly handle the class.
  // Skip if you are not using TS.
  protected override readonly _editor: Editor;

  private readonly editorContainer;
  private readonly toolbarContainer;
  private readonly revisionHistoryContainer;

  // Assign the custom editor class.
  constructor(editor: Editor) {
    super(editor);

    //this brings with it the wrong container, which causes no css to be applied. Ideally we would add these ids on the documentEditorContent and move that, but the id needs to be different per editor
    if (editor.config.get('entityType') === 'change_control') {
      this.editorContainer = document.getElementById(
        'change-control-container',
      );
      this.toolbarContainer = document.getElementById('cc-editor-toolbar');
      this.revisionHistoryContainer = document.getElementById(
        'change-control-revision-history-container',
      );
    } else {
      this.editorContainer = document.getElementById(
        'document-multiroot-container',
      );
      this.toolbarContainer = document.getElementById('editor-toolbar');
      this.revisionHistoryContainer = document.getElementById(
        'document-revision-history-container',
      );
    }
    this._editor = editor;
  }

  public override defaultOnEnter() {
    // Implement your fullscreen logic here.
    // For DOM manipulation, use `this.moveToFullscreen()` helper to ensure the elements are properly cleaned up
    // after leaving fullscreen mode
    this._moveToScopedEditable(this.editorContainer!);
    this.moveToFullscreen(this.toolbarContainer!, 'toolbar');
    this._moveEditorBodyIntoFullscreen();
    this._moveToastPortalIntoFullscreen();

    return super.defaultOnEnter();
  }

  private _moveToScopedEditable(element: HTMLElement) {
    this.moveToFullscreen(element, 'editable');

    const app = document.getElementById('documentEditorApp');
    const slot = this.getWrapper().querySelector<HTMLElement>(
      '[data-ck-fullscreen="editable"]',
    );
    if (!app || !slot) {
      return;
    }

    let appScope = slot.querySelector<HTMLElement>(
      ':scope > [data-fullscreen-style-scope]',
    );
    if (!appScope) {
      appScope = document.createElement('div');
      appScope.dataset.fullscreenStyleScope = '';
      appScope.className = app.className;
      appScope.style.display = 'contents';

      const chakraScope = document.createElement('chakra-scope');
      chakraScope.style.display = 'contents';
      appScope.appendChild(chakraScope);
      slot.appendChild(appScope);
    }

    appScope.firstElementChild!.appendChild(element);
  }

  private _moveEditorBodyIntoFullscreen() {
    const container = this._editor.ui.view.body.bodyCollectionContainer;
    if (!container) {
      return;
    }

    const placeholderName = 'editor-body';
    const wrapper = this.getWrapper();
    if (!wrapper.querySelector(`[data-ck-fullscreen="${placeholderName}"]`)) {
      const slot = document.createElement('div');
      slot.dataset.ckFullscreen = placeholderName;
      wrapper.appendChild(slot);
    }
    this.moveToFullscreen(container, placeholderName);
  }

  private _moveToastPortalIntoFullscreen() {
    const toastPortal = document.getElementById('qualio-toast-portal');
    if (!toastPortal) {
      return;
    }

    const placeholderName = 'toast-portal';
    const wrapper = this.getWrapper();
    if (!wrapper.querySelector(`[data-ck-fullscreen="${placeholderName}"]`)) {
      const slot = document.createElement('div');
      slot.dataset.ckFullscreen = placeholderName;
      wrapper.appendChild(slot);
    }
    this.moveToFullscreen(toastPortal, placeholderName);
  }

  public overrideRevisionHistoryCallbacks(): void {
    this._editor.config.set(
      'revisionHistory.showRevisionViewerCallback',
      async () => {
        const revisionViewerEditor = await this._showRevisionViewerCallback!();

        if (this._editor.plugins.has('DocumentOutlineUI')) {
          (
            this.getWrapper().querySelector(
              '.ck-fullscreen__document-outline-header',
            ) as HTMLElement
          ).style.display = 'none';
        }

        this.restoreMovedElementLocation('editable');
        this.restoreMovedElementLocation('toolbar');
        this.restoreMovedElementLocation('right-sidebar');

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (this._annotationsUIsData) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          this._restoreAnnotationsUIs();
        }

        if (this._editor.ui.view.menuBarView) {
          this._editor.ui.view.menuBarView.disable();
        }

        // this will need to be adjusted to handle change control editors
        this._moveToScopedEditable(this.revisionHistoryContainer!);
        this.moveToFullscreen(this.toolbarContainer!, 'toolbar');
        this.moveToFullscreen(
          this._editor.config.get(
            'revisionHistory.viewerSidebarContainer',
          ) as any,
          'right-sidebar',
        );

        return revisionViewerEditor;
      },
    );

    // * Hide revision viewer editable, toolbar and sidebar;
    // * Enable menu bar;
    // * Show editor's editable, toolbar and sidebar.
    // Code coverage is provided in the commercial package repository as integration unit tests.
    /* istanbul ignore next -- @preserve */
    this._editor.config.set(
      'revisionHistory.closeRevisionViewerCallback',
      async () => {
        this.restoreMovedElementLocation('toolbar');
        this.restoreMovedElementLocation('editable');
        this.restoreMovedElementLocation('right-sidebar');

        if (this._editor.plugins.has('DocumentOutlineUI')) {
          (
            this.getWrapper().querySelector(
              '.ck-fullscreen__document-outline-header',
            ) as HTMLElement
          ).style.display = '';
        }

        // this will need to be adjusted to handle change control editors
        this._moveToScopedEditable(this.editorContainer!);
        this.moveToFullscreen(this.toolbarContainer!, 'toolbar');

        await this._closeRevisionViewerCallback!();

        if (this._editor.plugins.has('AnnotationsUIs')) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          this._overrideAnnotationsUIs();
        }

        if (this._editor.ui.view.menuBarView) {
          this._editor.ui.view.menuBarView.enable();
        }
      },
    );
  }

  // Code coverage is provided in the commercial package repository as integration unit tests.
  /* istanbul ignore next -- @preserve */
  private _setupPaginationBodyCollection() {
    if (
      !this._editor.plugins.has('Pagination') ||
      !(this._editor.plugins.get('Pagination') as any).isEnabled
    ) {
      return;
    }

    const paginationRenderer = this._editor.plugins.get(
      'PaginationRenderer',
    ) as any;

    paginationRenderer.setupScrollableAncestor();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this._paginationBodyCollection = new BodyCollection(this._editor.locale);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this._paginationBodyCollection.attachToDom();
    paginationRenderer.linesRepository.setViewCollection(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this._paginationBodyCollection,
    );

    this._editor.once('destroy', () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this._paginationBodyCollection!.detachFromDom();
    });

    this.moveToFullscreen(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this._paginationBodyCollection.bodyCollectionContainer,
      'body-wrapper',
    );
  }

  private _setupRevisionHistoryIfPresent() {
    if (!this._editor.plugins.has('RevisionHistory')) {
      return;
    }

    // Code coverage is provided in the commercial package repository as integration unit tests.
    /* istanbul ignore if -- @preserve */
    if (
      (this._editor.plugins.get('RevisionHistory') as any).isRevisionViewerOpen
    ) {
      // Keep in mind that closing the revision history viewer is an asynchronous operation.
      (
        this._editor.config.get(
          'revisionHistory.closeRevisionViewerCallback',
        ) as any
      )();
    }

    this.overrideRevisionHistoryCallbacks();
  }

  private _setupSourceEditingCallback() {
    if (
      this._editor.plugins.has('SourceEditing') &&
      this._editor.plugins.has('DocumentOutlineUI')
    ) {
      (this._editor.plugins.get('SourceEditing') as any).on(
        'change:isSourceEditingMode',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this._sourceEditingCallback,
      );
    }
  }

  private _shouldHideContainerElement(element: HTMLElement): boolean {
    return (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      element !== this._wrapper &&
      !element.classList.contains('ck-body-wrapper') &&
      !element.classList.contains('ckbox-wrapper') &&
      element.id !== 'container' &&
      // Already hidden elements are not hidden again to avoid accidentally showing them after leaving fullscreen.
      element.style.display !== 'none'
    );
  }

  // Hide all other elements in the container to ensure they don't create an empty unscrollable space.
  private _hideOtherContainerElements() {
    for (const element of this._editor.config.get('fullscreen.container')!
      .children) {
      // Do not hide body wrapper and ckbox wrapper to keep dialogs, balloons etc visible.
      if (this._shouldHideContainerElement(element as HTMLElement)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this._hiddenElements.set(
          element as HTMLElement,
          (element as HTMLElement).style.display,
        );
        (element as HTMLElement).style.display = 'none';
      }
    }
  }

  public override enable() {
    this.defaultOnEnter();
    // Block scroll if the fullscreen container is the body element. Otherwise the document has to stay scrollable.
    if (this._editor.config.get('fullscreen.container') === document.body) {
      document.body.classList.add('ck-fullscreen');
      document.body.parentElement!.classList.add('ck-fullscreen');
    }

    if (this._editor.plugins.has('Dialog')) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this._registerFullscreenDialogPositionAdjustments();
    }

    // Code coverage is provided in the commercial package repository as integration unit tests.
    /* istanbul ignore if -- @preserve */
    if (this._editor.plugins.has('PresenceListUI')) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this._generatePresenceListContainer();
    }

    // Code coverage is provided in the commercial package repository as integration unit tests.
    /* istanbul ignore if -- @preserve */
    if (this._editor.plugins.has('DocumentOutlineUI')) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this._generateDocumentOutlineContainer();
    }

    this._setupPaginationBodyCollection();

    // Code coverage is provided in the commercial package repository as integration unit tests.
    /* istanbul ignore if -- @preserve */
    if (this._editor.plugins.has('AnnotationsUIs')) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this._overrideAnnotationsUIs();
    }

    this._setupRevisionHistoryIfPresent();
    this._setupSourceEditingCallback();
    this._hideOtherContainerElements();

    if (this._editor.config.get('fullscreen.onEnterCallback')) {
      this._editor.config.get('fullscreen.onEnterCallback')!(this.getWrapper());
    }
  }
}

export class MultirootFullscreen extends Plugin {
  // Ensure 'toggleFullscreen' command, registered in `FullscreenEditing` plugin, is already available in the editor.
  public static get requires() {
    return [FullscreenEditing] as const;
  }

  init() {
    // Substitute the default editor handler with a custom one.
    const toggleFullscreen = this.editor.commands.get('toggleFullscreen');
    if (!toggleFullscreen) {
      throw new Error('Fullscreen command is not registered.');
    }
    toggleFullscreen.fullscreenHandler = new MultirootFullscreenAdapter(
      this.editor,
    );
  }
}
